import React, { useState, useEffect, useRef, useCallback } from "react";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from "date-fns/locale";
import { useClient } from "../../context/ClientProvider";
import { useAccount } from "../../context/AccountProvider";
import { useProfile } from "../../context/ProfileProvider";
import { useCallMe } from "../../context/CallMeProvider";
import { useParty } from "../../context/PartyProvider";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import PropTypes from "prop-types";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from '@mui/icons-material/Cancel';
import {
    Alert,
    Dialog,
    DialogTitle,
    DialogContentText,
    Select,
    MenuItem,
    DialogActions,
    IconButton,
    TextField,
    Typography,
    Box
} from "@mui/material";
import ActionButton from "../account/ActionButton";
import dayjs from "dayjs";

const CallMeDialog = ({ open, onClose, onCallbackSet, clientRef: propClientRef, dateOfBirth: propDateOfBirth, requestedCallbackDatetime = null }) => {
    const { postCallback, getCallbacks } = useCallMe();
    const { useGetParty } = useParty();
    const { party } = useGetParty();
    const { useGetAccount } = useAccount();
    const { account } = useGetAccount();
    const { useGetContactNumber } = useProfile();
    const { contactNumber } = useGetContactNumber();
    const { useGetConfig } = useClient();
    const { clientConfig } = useGetConfig();

    const hasValidCallback = useRef(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [time, setTime] = useState("");
    const [selectedDate, setSelectedDate] = useState("");
    const [showDateTimePicker, setShowDateTimePicker] = useState(false);
    const [homeNumber, setHomeNumber] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [callbackRequested, setCallbackRequested] = useState(false);
    const [callbackBlocked, setCallbackBlocked] = useState(false);
    const [noAvailableSlots, setNoAvailableSlots] = useState(false);
    const [isClosedMessageVisible, setIsClosedMessageVisible] = useState(false);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [confirmedCallback, setConfirmedCallback] = useState(null);
    const [asapCallback, setAsapCallback] = useState(false);
    const [bankHolidays, setBankHolidays] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const truncatedDateTime = new Date().toISOString().split('.')[0] + 'Z';
    const resolvedClientRef = account?.accountReference || propClientRef;

    const resolvedDateOfBirth =
        party?.dob ||
        propDateOfBirth ||
        account?.dateOfBirth ||
        null;

    useEffect(() => {
        if (open) {
            setCallbackBlocked(false);
            setCallbackRequested(false);
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            setCallbackBlocked(false);
            setCallbackRequested(false);
            setShowDateTimePicker(false);
        }
    }, [open]);

    useEffect(() => {
        // Avoid overwriting confirmedCallback from fetchExistingCallback
        if (confirmedCallback) return;
        if (requestedCallbackDatetime) {
            const now = new Date();
            const callbackDate = new Date(requestedCallbackDatetime);
            const diffInMinutes = Math.abs((callbackDate - now) / (1000 * 60));
            const inferredAsap = diffInMinutes <= 10;

            setConfirmedCallback({
                datetime: requestedCallbackDatetime,
                isAsap: inferredAsap,
            });
            setCallbackBlocked(true);
        }

    }, [requestedCallbackDatetime, confirmedCallback]);

    useEffect(() => {
        if (contactNumber) {
            if (Array.isArray(contactNumber)) {
                const home = contactNumber.find((c) => c.type === "Home");
                if (home) setHomeNumber(home.number || "");
            } else if (typeof contactNumber === "object") {
                setHomeNumber(contactNumber.number || "");
            }
        }
    }, [contactNumber]);

    useEffect(() => {
        const fetchExistingCallback = async () => {
            if (!resolvedClientRef || !open) return;

            try {
                const response = await getCallbacks(resolvedClientRef);
                const existingCallbacks = response?.callbacks || [];

                if (Array.isArray(existingCallbacks) && existingCallbacks.length > 0) {
                    const futureCallbacks = existingCallbacks

                    if (futureCallbacks.length > 0) {
                        const latest = futureCallbacks[0];

                        setConfirmedCallback({
                            datetime: latest.requestedCallbackDatetime,
                            isAsap: latest.reason?.toLowerCase().includes("as soon as possible") || false,
                        });

                        setCallbackBlocked(true);
                        hasValidCallback.current = true;
                        return;
                    }
                }

                setConfirmedCallback(null);
                setCallbackBlocked(false);
                hasValidCallback.current = false;
            } catch (error) {
                console.error("Failed to fetch existing callbacks:", error);
                setConfirmedCallback(null);
                setCallbackBlocked(false);
                hasValidCallback.current = false;
            }
        };

        fetchExistingCallback();
    }, [resolvedClientRef, open]);

    const slotTemplatesByDay = {
        1: [90, 120, 180, 180], // Monday (in minutes): 4 slots
        2: [90, 120, 180, 180],
        3: [90, 120, 180, 180],
        4: [90, 120, 180, 180],
        5: [90, 120, 180, 180],
        6: [120, 120],          // Saturday: 2 x 2hr
        // 0 (Sunday) not included
    };

    const getSlots = () => {
        if (!selectedDate || !clientConfig?.layout?.callMe) return;

        const selectedDateObj = dayjs(selectedDate, "DD/MM/YYYY");
        if (!selectedDateObj.isValid()) return;
        const selectedDateISO = selectedDateObj.format("YYYY-MM-DD");
        const selectedDay = selectedDateObj.day(); // 0 = Sunday

        if (selectedDay === 0 || bankHolidays.includes(selectedDateISO)) {
            setAvailableSlots([]);
            setNoAvailableSlots(true);
            setIsClosedMessageVisible(true);
            return;
        }

        const todayAttributes = clientConfig.layout.callMe.dailyAttributes[selectedDay - 1];
        const openingTimeStr = todayAttributes?.openingTime;
        const closingTimeStr = todayAttributes?.closingTime;

        if (!openingTimeStr || !closingTimeStr) {
            setAvailableSlots([]);
            setNoAvailableSlots(true);
            setIsClosedMessageVisible(true);
            return;
        }

        const parseTime = (str) => {
            const [h, m] = str.split(":").map(Number);
            return h * 60 + m;
        };

        const formatTime = (minutes) => {
            const h = Math.floor(minutes / 60).toString().padStart(2, '0');
            const m = (minutes % 60).toString().padStart(2, '0');
            return `${h}:${m}`;
        };

        const now = dayjs();
        const isToday = selectedDateObj.isSame(now, "day");
        const currentMinutes = now.hour() * 60 + now.minute();

        const openingMinutes = parseTime(openingTimeStr);
        const closingMinutes = parseTime(closingTimeStr);

        const preferredDurations = slotTemplatesByDay[selectedDay] || [];

        const slots = [];
        let cursor = openingMinutes;

        for (let duration of preferredDurations) {
            const end = cursor + duration;

            const isSlotInFuture = !isToday || cursor > currentMinutes;
            const isWithinBusinessHours = end <= closingMinutes;

            if (isSlotInFuture && isWithinBusinessHours) {
                slots.push({
                    id: `${formatTime(cursor)}-${formatTime(end)}`,
                    startTime: formatTime(cursor),
                    endTime: formatTime(end),
                });
                cursor = end; // Move forward by duration
            } else {
                cursor = end; // 👈 Just skip this one, keep trying next durations
                continue;
            }
        }

        setAvailableSlots(slots);
        setNoAvailableSlots(slots.length === 0);
        setIsClosedMessageVisible(slots.length === 0);
        if (!slots.length) setTime("");
    };

    useEffect(() => {
        getSlots();
    }, [selectedDate, clientConfig, bankHolidays]);

    const checkOpeningStatus = useCallback(() => {
        const currentTime = new Date();
        let currentDay = currentTime.getDay();
        currentDay = currentDay === 0 ? 6 : currentDay - 1; // Adjust Sunday

        const todayAttributes = clientConfig?.layout?.callMe?.dailyAttributes[currentDay];
        const dailyOpening = todayAttributes?.openingTime;
        const dailyClosing = todayAttributes?.closingTime;

        if (!dailyOpening || !dailyClosing) {
            setIsClosedMessageVisible(true);
            setNoAvailableSlots(true);
            return;
        }

        const getTodayTime = (timeStr) => {
            const [hours, minutes] = timeStr.split(':').map(Number);
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0, 0);
        };

        const openingTime = getTodayTime(dailyOpening);
        const closingTime = getTodayTime(dailyClosing);

        const isClosed = currentTime < openingTime || currentTime >= closingTime;

        setIsClosedMessageVisible(isClosed);
        setNoAvailableSlots(isClosed);
    }, [clientConfig]);

    useEffect(() => {
        if (!clientConfig) return;

        checkOpeningStatus(); // run immediately on mount

        const intervalId = setInterval(() => {
            checkOpeningStatus();
        }, 60 * 1000); // every minute

        return () => clearInterval(intervalId);
    }, [checkOpeningStatus, clientConfig]);

    const handleCallNow = async () => {
        if (callbackBlocked || callbackRequested || isSubmitting) return;

        setCallbackBlocked(true);     // ⛔ Block both buttons immediately
        setCallbackRequested(true);   // ✅ Prevent duplicates visually

        const validationResult = validatePhoneNumber(homeNumber);
        if (validationResult) {
            setPhoneError(validationResult);
            setCallbackBlocked(false);   // 🔓 Re-enable if validation fails
            setCallbackRequested(false);
            return;
        }

        setIsSubmitting(true);

        if (!resolvedDateOfBirth || isNaN(new Date(resolvedDateOfBirth).getTime())) {
            console.error("Invalid or missing date of birth.");
            setErrorDialogOpen(true);
            setIsSubmitting(false);
            return;
        }

        try {
            const requestDatetime = new Date();
            requestDatetime.setMinutes(requestDatetime.getMinutes() + 5);
            const truncatedRequestDatetime = requestDatetime.toISOString().split('.')[0] + 'Z';

            await postCallback({
                phoneNumber: homeNumber,
                clientRef: resolvedClientRef,
                dob: resolvedDateOfBirth,
                requestDatetime: truncatedDateTime,
                reason: "Callback as soon as possible",
                requestedCallbackDatetime: truncatedRequestDatetime,
            });

            hasValidCallback.current = true;
            setAsapCallback(true);
            setCallbackBlocked(true);
            setCallbackRequested(true);
            setErrorDialogOpen(false);

            setConfirmedCallback({
                datetime: truncatedRequestDatetime,
                isAsap: true,
            });

            if (onCallbackSet) onCallbackSet(true, asapCallback);
        } catch (error) {
            console.error("Callback request failed", error);
            setErrorDialogOpen(true);
        } finally {
            setIsSubmitting(false); // ✅ Ensure it's always reset
        }
    };

    const handleCallLater = async () => {
        if (callbackBlocked || callbackRequested || isSubmitting) return;

        setCallbackBlocked(true);
        setCallbackRequested(true);

        const validationResult = validatePhoneNumber(homeNumber);
        if (validationResult) {
            setPhoneError(validationResult);
            setCallbackBlocked(false);
            setCallbackRequested(false);
            return;
        }

        if (!resolvedDateOfBirth || isNaN(new Date(resolvedDateOfBirth).getTime())) {
            console.error("Invalid or missing date of birth.");
            setErrorDialogOpen(true);
            setIsSubmitting(false);
            return;
        }
        if (selectedDate && time) {
            const selectedDateTime = dayjs(selectedDate, "DD/MM/YYYY");
            if (!selectedDateTime.isValid()) return;
            const [hours, minutes] = time.split(" - ")[0].split(":");
            const callbackDateTime = selectedDateTime.hour(Number(hours)).minute(Number(minutes)).second(0);
            const truncatedRequestedCallbackDatetime = callbackDateTime.toISOString().split('.')[0] + 'Z';

            try {
                await postCallback({
                    phoneNumber: homeNumber,
                    clientRef: resolvedClientRef,
                    dob: resolvedDateOfBirth,
                    requestDatetime: truncatedDateTime,
                    requestedCallbackDatetime: truncatedRequestedCallbackDatetime,
                    reason: "Callback at the scheduled time",
                });

                hasValidCallback.current = true;
                setAsapCallback(false);
                setCallbackBlocked(true);
                setCallbackRequested(true);
                setErrorDialogOpen(false);
                setConfirmedCallback({
                    datetime: truncatedRequestedCallbackDatetime,
                    isAsap: false,
                });

                if (onCallbackSet) onCallbackSet(true);
            } catch (error) {
                console.error("Scheduled callback request failed", error);
                setErrorDialogOpen(true);
            } finally {
                setIsSubmitting(false); // ✅ Allow re-interaction only if needed
            }
        }
    };

    const showDate = () => {
        setShowDateTimePicker(true);
    };

    const formatDate = (dateTime, isAsap) => {
        if (isAsap) return "ASAP";
        if (!dateTime) return "Invalid Date";

        const date = new Date(dateTime);
        if (isNaN(date.getTime())) {
            console.warn("Invalid Date passed to formatDate:", dateTime);
            return "Invalid Date";
        }

        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const formatTime = (dateTime, isAsap) => {
        if (isAsap) return "ASAP";
        if (!dateTime) return "Invalid Time";

        const date = new Date(dateTime);
        if (isNaN(date.getTime())) {
            console.warn("Invalid Time passed to formatTime:", dateTime);
            return "Invalid Time";
        }

        return date.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
    };


    const validatePhoneNumber = (value) => {
        const cleaned = value.replace(/\s+/g, '');

        if (!cleaned) return "Phone number is required.";
        // Still block known bad prefixes (after replacing + with 0 if needed)
        const localEquivalent = cleaned.startsWith('+44') ? cleaned.replace('+44', '0') : cleaned;
        if (/^(080|060|050)/.test(localEquivalent)) {
            return "Invalid phone number. Numbers starting with 080, 060, or 050 are not allowed.";
        }

        // Accept formats like +[countrycode][number], digits only
        const internationalPattern = /^\+?\d{11,15}$/;
        if (!internationalPattern.test(cleaned)) {
            return "Invalid phone number. Use digits with optional + and 11–15 digits total.";
        }

        return "";
    };

    useEffect(() => {
        const rawHolidays = clientConfig?.layout?.callMe?.bankHolidays || [];

        const normalized = rawHolidays.map((entry) => {
            let dateStr;

            if (typeof entry === "string") {
                dateStr = entry;
            } else if (typeof entry === "object" && entry.date) {
                dateStr = entry.date;
            } else {
                return null;
            }

            // Parse and normalize to yyyy-mm-dd
            if (dateStr.includes("/")) {
                const [day, month, year] = dateStr.split("/");
                return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
            } else if (dateStr.includes("-")) {
                return dateStr.slice(0, 10); // Already ISO
            }

            return null;
        }).filter(Boolean);

        setBankHolidays(normalized);
    }, [clientConfig]);

    const disableSundaysAndPastDates = () => {
        const today = new Date();

        const maxDateObj = new Date();
        maxDateObj.setDate(today.getDate() + 6);
        const maxDateISO = maxDateObj.toISOString().split('T')[0];
        const minDateObj = new Date(today);

        if (minDateObj.getDay() === 0) {
            minDateObj.setDate(today.getDate() + 1);
        }
        const minDateISO = minDateObj.toISOString().split('T')[0];

        return { minDate: minDateISO, maxDate: maxDateISO };
    };

    const { minDate, maxDate } = disableSundaysAndPastDates();

    const shouldDisableDate = useCallback((date) => {
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();
        const isSunday = date.getDay() === 0;
        const formatted = date.toISOString().split('T')[0];

        if (isSunday || bankHolidays.includes(formatted)) return true;

        const dayIndex = date.getDay(); // Sunday = 0
        const attrs = clientConfig?.layout?.callMe?.dailyAttributes?.[dayIndex - 1];

        if (!attrs?.availableCallSlots?.length) return true;


        const parseTime = (t) => {
            const [h, m] = t.split(":").map(Number);
            return h * 60 + m;
        };

        const openMin = parseTime(attrs.openingTime);
        const closeMin = parseTime(attrs.closingTime);
        const nowMin = now.getHours() * 60 + now.getMinutes();

        const validSlots = attrs.availableCallSlots.filter(slot => {
            const start = parseTime(slot.startTime);
            const end = parseTime(slot.endTime);

            const inTimeRange = start >= openMin && end <= closeMin;
            const notExpired = !isToday || start > nowMin;

            return inTimeRange && notExpired;
        });

        return validSlots.length === 0;
    }, [clientConfig, bankHolidays]);

    const getMaxLength = () => {
        if (homeNumber.startsWith("+44")) return 13;
        if (homeNumber.startsWith("0")) return 11;
        return 15;
    };

    const getDatePickerValue = () => {
        if (!selectedDate) return null;
        const parsed = dayjs(selectedDate, "DD/MM/YYYY");
        return parsed.isValid() ? parsed.toDate() : null;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            sx={{ padding: { xs: 0, md: 3 }, textAlign: "center", margin: "auto", maxWidth: "500px", width: "100%" }}
        >
            <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                <CloseIcon sx={{ fontSize: "2rem", color: { xs: "#fff", md: "#000" } }} />
            </IconButton>

            {!confirmedCallback ? (
                <>
                    {/* 👇 This was the second block — now first (form UI) */}
                    <PhoneInTalkIcon sx={{ width: { md: "100px", sm: "80px", xs: "60px" }, height: { md: "100px", sm: "80px", xs: "60px" }, ml: "auto", mr: "auto", mt: { md: 7, xs: 4, sm: 6 }, color: "primary.dark" }} />
                    <DialogTitle variant="h4" sx={{ fontWeight: "bold", fontSize: { xs: "25px" } }}>Call me back</DialogTitle>
                    <DialogContentText sx={{ maxWidth: "90%", padding: 1, color: "#000", ml: "auto", mr: "auto", fontSize: { xs: "14px" } }}>
                        We will endeavour to call you back as close to your requested time as possible, however during peak periods there may be a delay before we are able to call you back.
                    </DialogContentText>

                    {isClosedMessageVisible && (
                        <DialogContentText sx={{ width: { md: "300px", xs: "350px" }, ml: "auto", mr: "auto", padding: 2, color: "#ff0000", overflow: "hidden", minHeight: "80px" }}>
                            We are currently closed, please use SCHEDULE A CALLBACK to select an available timeslot.  
                        </DialogContentText>
                    )}

                    <Typography variant="body1" sx={{ fontWeight: "bold", textAlign: "left", ml: { md: 3, sm: 2, xs: 2 }, fontSize: { xs: "14px" } }}>Phone Number:</Typography>
                    <TextField
                        type="text"
                        value={homeNumber}
                        required
                        sx={{ ml: "auto", mr: "auto", mb: 2, width: "90%" }}
                        variant="outlined"
                        placeholder={homeNumber}
                        onChange={(e) => {
                            const value = e.target.value;
                            setHomeNumber(value);
                            const errorMsg = validatePhoneNumber(value);
                            setPhoneError(errorMsg);
                        }}
                        inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9+]*",
                            maxLength: getMaxLength(),
                        }}
                        error={!!phoneError}
                    />
                    {phoneError && (
                        <Alert variant="filled" severity="error" sx={{ ml: "auto", mr: "auto", mb: 2, maxWidth: "90%", minWidth: "90%" }}>
                            {phoneError}
                        </Alert>
                    )}

                    {!showDateTimePicker && (
                        <DialogActions sx={{ display: "flex", width: "100%", alignItems: "center", mb: 4 }}>
                            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", gap: 2, p: { xs: 2, md: 1 } }}>
                                <ActionButton
                                    sx={{ ml: 5 }}
                                    variant='contained'
                                    buttonColor='secondary.main'
                                    buttonTextColor='primary.contrastText'
                                    borderColor='transparent'
                                    hoverColor='primary.contrastText'
                                    hoverBorderColor='secondary.main'
                                    hoverTextColor='secondary.main'
                                    buttonTitle='Call me ASAP'
                                    disabled={
                                        callbackBlocked ||
                                        callbackRequested ||
                                        isSubmitting ||
                                        !homeNumber.trim() ||
                                        !!phoneError ||
                                        isClosedMessageVisible ||
                                        noAvailableSlots
                                    }
                                    onClick={handleCallNow}
                                />
                                <ActionButton
                                    sx={{ ml: 5 }}
                                    borderColor='primary.dark'
                                    buttonColor='transparent'
                                    buttonTextColor='primary.dark'
                                    buttonTitle='Schedule a Callback'
                                    variant='outlined'
                                    hoverColor='primary.dark'
                                    hoverBorderColor={"primary.dark"}
                                    hoverTextColor='secondary.contrastText'
                                    disabled={
                                        callbackBlocked ||
                                        callbackRequested ||
                                        isSubmitting ||
                                        !homeNumber.trim() ||
                                        !!phoneError
                                    }
                                    onClick={showDate}
                                />
                            </Box>
                        </DialogActions>
                    )}

                    {showDateTimePicker && (
                        <>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: "16px" } }}>When would you like a call</Typography>
                            <Typography variant="body1" sx={{ fontWeight: "bold", textAlign: "left", ml: { md: 3, sm: 2, xs: 2 }, fontSize: { xs: "14px" } }}>Date:</Typography>
                            <Box sx={{ width: "90%", ml: "auto", mr: "auto" }}>
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                                    <DatePicker
                                        value={getDatePickerValue()}
                                        onChange={(newValue) => {
                                            if (newValue) {
                                                setSelectedDate(dayjs(newValue).format("DD/MM/YYYY"));
                                            } else {
                                                setSelectedDate("");
                                            }
                                        }}
                                        shouldDisableDate={shouldDisableDate}
                                        disablePast
                                        slotProps={{
                                            textField: {
                                                InputProps: {
                                                    readOnly: true,
                                                    disableUnderline: true,
                                                },
                                                inputProps: {
                                                    readOnly: true,
                                                    tabIndex: -1
                                                }
                                            }
                                        }}
                                        minDate={minDate ? dayjs(minDate, "YYYY-MM-DD").toDate() : undefined}
                                        maxDate={maxDate ? dayjs(maxDate, "YYYY-MM-DD").toDate() : undefined}
                                        inputFormat="dd/MM/yyyy"
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                sx={{ width: "100%", mb: 2 }}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            </Box>
                            <Typography variant="body1" sx={{ fontWeight: "bold", textAlign: "left", ml: { md: 3, sm: 2, xs: 2 }, fontSize: { xs: "14px" } }}>Time:</Typography>
                            <Box sx={{ width: "80%", ml: "auto", mr: "auto" }}>
                                <Select
                                    value={time}
                                    onChange={(e) => setTime(e.target.value)}
                                    disabled={!selectedDate || noAvailableSlots}
                                    sx={{ width: "100%", mb: 2 }}
                                >
                                    {availableSlots.map((slot) => (
                                        <MenuItem key={slot.id} value={`${slot.startTime} - ${slot.endTime}`}>
                                            {slot.startTime} - {slot.endTime}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>

                            <DialogActions sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", gap: 2, p: { xs: 2, md: 1 } }}>
                                    <ActionButton
                                        sx={{ ml: 5 }}
                                        borderColor='primary.dark'
                                        buttonColor='transparent'
                                        buttonTextColor='primary.dark'
                                        buttonTitle='Call me back later'
                                        variant='outlined'
                                        hoverColor='primary.dark'
                                        hoverBorderColor={"primary.dark"}
                                        hoverTextColor='secondary.contrastText'
                                        onClick={handleCallLater}
                                        disabled={
                                            callbackRequested ||
                                            callbackBlocked ||
                                            isSubmitting ||
                                            !homeNumber.trim() ||
                                            !selectedDate ||
                                            !time ||
                                            !!phoneError
                                        }
                                    />
                                </Box>
                            </DialogActions>
                        </>
                    )}
                </>
            ) : (
                    <Box sx={{ maxWidth: "100%", textAlign: "center", pb: 10, pl: 1, pr: 1 }}>
                        {/* 👇 This was the first block — now shown second (confirmation UI) */}
                        <CheckCircleIcon color="success" sx={{
                            width: { md: "100px", sm: "80px", xs: "60px" },
                            height: { md: "100px", sm: "80px", xs: "60px" },
                            ml: "auto", mr: "auto", mt: { md: 7, xs: 4, sm: 6 },
                        }} />
                        <DialogTitle variant="h5" sx={{ fontWeight: "bold", fontSize: { md: "30px", xs: "20px", sm: "25px" } }}>Call back confirmed!</DialogTitle>
                        <DialogContentText sx={{ maxWidth: "90%", padding: 3, color: "#000", ml: "auto", mr: "auto", fontSize: { xs: "14px" } }}>
                            We will endeavour to call you back as close to your requested time as possible, however during peak periods there may be a delay before we are able to call you back.
                        </DialogContentText>
                        <Typography variant="body1" sx={{ fontWeight: "bold", textAlign: "left", ml: { md: 3, sm: 2, xs: 2 }, fontSize: { xs: "14px" } }}>Scheduled Date:</Typography>
                        <TextField
                            type="text"
                            size="small"
                            disabled
                            sx={{
                                width: "90%", mb: 2, backgroundColor: "#E2E2E2",
                                "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000" }
                            }}
                            variant="outlined"
                            value={formatDate(confirmedCallback?.datetime, confirmedCallback?.isAsap)}
                        />
                        <Typography variant="body1" sx={{ fontWeight: "bold", textAlign: "left", ml: { md: 3, sm: 2, xs: 2 }, fontSize: { xs: "14px" } }}>Scheduled Time:</Typography>
                        <TextField
                            type="text"
                            size="small"
                            disabled
                            sx={{
                                width: "90%", mb: 2, backgroundColor: "#E2E2E2",
                                "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000" }
                            }}
                            variant="outlined"
                            value={formatTime(confirmedCallback?.datetime, confirmedCallback?.isAsap)}
                        />
                </Box>
            )}

            {errorDialogOpen && (
                <Dialog open={true} onClose={() => setErrorDialogOpen(false)}
                    sx={{ padding: { xs: 0, md: 3 }, textAlign: "center", margin: "auto", maxWidth: "500px", width: "100%" }}

                >
                    <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                        <CloseIcon sx={{ fontSize: "2rem", color: { xs: "#fff", md: "#000" } }} />
                    </IconButton>
                    <Box>
                        <CancelIcon
                            color="error"
                            sx={{
                                width: { md: "100px", sm: "80px", xs: "60px" },
                                height: { md: "100px", sm: "80px", xs: "60px" },
                                ml: "auto", mr: "auto", mt: { md: 7, xs: 4, sm: 6 },
                            }}
                        />
                        <DialogTitle variant="h5" sx={{ fontWeight: "bold", fontSize: { md: "30px", xs: "20px", sm: "25px" } }}>Call back Failed!</DialogTitle>
                        <DialogContentText sx={{ maxWidth: "90%", padding: 3, color: "#000", ml: "auto", mr: "auto", fontSize: { xs: "14px" } }}>
                            We were unable to set your callback due to a technical issue. Please try again, or if the issue persists,
                            contact us directly during opening times.
                        </DialogContentText>
                    </Box>
                </Dialog>
            )}
        </Dialog>

    );
}

CallMeDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onCallbackSet: PropTypes.func.isRequired,
    clientRef: PropTypes.string,
    dateOfBirth: PropTypes.string,
    callbackScheduled: PropTypes.bool,
    requestedCallbackDatetime: PropTypes.string,
};

export default CallMeDialog;